import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Theme + font-size accessibility provider.
 *
 * Themes are applied via [data-theme] on <html> (see src/styles/tokens.css).
 * Font-size escalator via [data-font-size] on <html>.
 *
 *   const { theme, setTheme, fontSize, setFontSize } = useTheme();
 *
 * Theme is persisted in localStorage and respects `prefers-color-scheme` for
 * the default. `system` mode follows OS changes live.
 */

export type ThemeMode = "light" | "dark" | "high-contrast" | "system";
export type FontSize  = "md" | "lg" | "xl" | "xxl";

const THEME_KEY = "mhpol.theme";
const FONT_KEY  = "mhpol.fontSize";

interface Ctx {
  theme: ThemeMode;
  setTheme: (m: ThemeMode) => void;
  /** Resolved theme — "system" collapses to "light" or "dark". */
  resolvedTheme: "light" | "dark" | "high-contrast";
  fontSize: FontSize;
  setFontSize: (s: FontSize) => void;
}

const ThemeContext = createContext<Ctx | null>(null);

function readTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_KEY) as ThemeMode | null;
    if (v === "light" || v === "dark" || v === "high-contrast" || v === "system") return v;
  } catch { /* noop */ }
  return "system";
}
function readFont(): FontSize {
  try {
    const v = localStorage.getItem(FONT_KEY) as FontSize | null;
    if (v === "md" || v === "lg" || v === "xl" || v === "xxl") return v;
  } catch { /* noop */ }
  return "md";
}

function resolveSystem(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState]     = useState<ThemeMode>(() => readTheme());
  const [fontSize, setFontSizeRaw] = useState<FontSize>(() => readFont());
  const [sysDark, setSysDark]      = useState<boolean>(() => resolveSystem() === "dark");

  // Live-track system color scheme when in "system" mode.
  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = () => setSysDark(mql.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  const resolvedTheme: "light" | "dark" | "high-contrast" = useMemo(() => {
    if (theme === "system") return sysDark ? "dark" : "light";
    return theme;
  }, [theme, sysDark]);

  // Apply data-theme + data-font-size on <html>
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-font-size", fontSize);
  }, [fontSize]);

  const setTheme = useCallback((m: ThemeMode) => {
    setThemeState(m);
    try { localStorage.setItem(THEME_KEY, m); } catch { /* noop */ }
  }, []);

  const setFontSize = useCallback((s: FontSize) => {
    setFontSizeRaw(s);
    try { localStorage.setItem(FONT_KEY, s); } catch { /* noop */ }
  }, []);

  const value: Ctx = { theme, setTheme, resolvedTheme, fontSize, setFontSize };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Permissive fallback so unwrapped consumers don't crash.
    return {
      theme: "light", setTheme: () => {},
      resolvedTheme: "light",
      fontSize: "md", setFontSize: () => {},
    };
  }
  return ctx;
}
